import React, { useEffect, useState, useRef } from "react";
import { setGlobalLoading } from "../context/LoaderController";
import { Link } from "react-router-dom";
import {
  LOCALSTORAGE_SELECTED_STATE_CODE,
  LOCALSTORAGE_SELECTED_STATE_NAME,
  ROUTE_HOME,
  PAGE_TITLE_VIEW_NOMINATION,
  NO_STATE_SELECTED_MESSAGE,
  LOADING_MESSAGE,
  CHOOSE_BALLOT_LABEL,
  LOADING_NOMINATIONS_MESSAGE,
  NO_NOMINATIONS_FOUND_MESSAGE,
  ERROR_LOAD_NOMINATIONS,
  NOMINATION_BALLOT_OPTIONS,
  TABLE_HEADERS_NOMINATIONS,
  NOMINATION_STATUS_CLASSES,
  LOADING_DISTRICTS_MESSAGE,
  LOADING_ASSEMBLIES_MESSAGE,
} from "../components/constants";
import {
  fetchNominations,
  fetchDistricts,
  fetchAssemblies,
  fetchMandalams,
  fetchStateBallots,
  fetchNominationReferrerCount,
} from "../services/api";
import "../App.css";

/* ---------- Shared Select Input (SAME AS GetResult) ---------- */
function SelectInput({ label, value, options, onChange, disabled }) {
  return (
    <div className="form-column">
      <label style={{ display: "block", marginBottom: 6 }}>{label}</label>
      <select
        className="form-select"
        value={value}
        onChange={onChange}
        disabled={disabled}
      >
        <option value="">Select {label}</option>
        {options.map((opt) => (
          <option key={opt.code || opt.id} value={opt.code || opt.id}>
            {opt.name || opt.title}
          </option>
        ))}
      </select>
    </div>
  );
}

/* ---------- Ballot hierarchy ---------- */
const BALLOT_LEVEL = {
  SP: 0,
  SG: 0,
  DP: 1,
  DG: 1,
  AS: 2,
  MD: 3,
};

export default function ViewNomination() {
  const tableRef = useRef(null);

  const stateId =
    localStorage.getItem(LOCALSTORAGE_SELECTED_STATE_CODE) || "";
  const storedStateName =
    localStorage.getItem(LOCALSTORAGE_SELECTED_STATE_NAME) || "";

  const [ballotId, setBallotId] = useState("");
  const [districtId, setDistrictId] = useState("");
  const [assemblyId, setAssemblyId] = useState("");
  const [mandalamId, setMandalamId] = useState("");

  const [districts, setDistricts] = useState([]);
  const [assemblies, setAssemblies] = useState([]);
  const [mandalams, setMandalams] = useState([]);

  const [nominations, setNominations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [ballots, setBallots] = useState([]);

  const [showReferralCount, setShowReferralCount] = useState(false);
  const [referralCounts, setReferralCounts] = useState([]);
  const [referralLoading, setReferralLoading] = useState(false);

  /* ---------- NEW: Referrer modal state ---------- */
  const [showReferrerModal, setShowReferrerModal] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);

const isChhattisgarh =
  stateId === "CG" || storedStateName?.toLowerCase() === "chhattisgarh";

const pdfUrl =
  "https://drive.google.com/file/d/1bE2PyJLXgvVNA-Tq9RQALu9fh55d89Jk/preview";

  useEffect(() => {
    if (!stateId) return;

    fetchStateBallots(stateId)
      .then(setBallots)
      .catch(console.error);
  }, [stateId]);

  const handleReferralCount = async () => {
    if (!stateId) return;

    // Open modal immediately
    setShowReferrerModal(true);

    // If already fetched, just show modal
    if (referralCounts.length > 0) return;

    setReferralLoading(true);

    try {
      const data = await fetchNominationReferrerCount(stateId);
      setReferralCounts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setReferralCounts([]);
    } finally {
      setReferralLoading(false);
    }
  };

  /* ---------- Dropdown config (SAME PATTERN AS GetResult) ---------- */
  const selectConfig = [
    {
      key: "districtId",
      label: "District",
      value: districtId,
      setValue: setDistrictId,
      options: districts,
      requiredFor: ["DP", "DG", "AS", "MD"],
      disabled: loading || districts.length === 0,
    },
    {
      key: "assemblyId",
      label: "Assembly",
      value: assemblyId,
      setValue: setAssemblyId,
      options: assemblies,
      requiredFor: ["AS", "MD"],
      disabled: loading || !districtId || assemblies.length === 0,
    },
    {
      key: "mandalamId",
      label: "Mandalam",
      value: mandalamId,
      setValue: setMandalamId,
      options: mandalams,
      requiredFor: ["MD"],
      disabled: loading || !assemblyId || mandalams.length === 0,
    },
  ];

  /* ---------- Reset on ballot change ---------- */
  useEffect(() => {
    setDistrictId("");
    setAssemblyId("");
    setMandalamId("");
    setDistricts([]);
    setAssemblies([]);
    setMandalams([]);
    setNominations([]);
  }, [ballotId]);

  /* ---------- Load districts ---------- */
  useEffect(() => {
    if (!stateId || BALLOT_LEVEL[ballotId] < 1) return;

    fetchDistricts(stateId).then(setDistricts).catch(console.error);
  }, [stateId, ballotId]);

  /* ---------- Load assemblies ---------- */
  useEffect(() => {
    if (!districtId || BALLOT_LEVEL[ballotId] < 2) {
      setAssemblies([]);
      return;
    }

    fetchAssemblies(stateId, districtId)
      .then(setAssemblies)
      .catch(console.error);
  }, [stateId, districtId, ballotId]);

  /* ---------- Load mandalams ---------- */
  useEffect(() => {
    if (!assemblyId || BALLOT_LEVEL[ballotId] < 3) {
      setMandalams([]);
      return;
    }

    fetchMandalams(stateId, districtId, assemblyId)
      .then(setMandalams)
      .catch(console.error);
  }, [stateId, districtId, assemblyId, ballotId]);

  /* ---------- Load nominations ---------- */
  useEffect(() => {
    if (!stateId || !ballotId) return;
    if (BALLOT_LEVEL[ballotId] >= 1 && !districtId) return;
    if (BALLOT_LEVEL[ballotId] >= 2 && !assemblyId) return;
    if (BALLOT_LEVEL[ballotId] >= 3 && !mandalamId) return;

    setGlobalLoading(true);
    setLoading(true);
    setError("");
    setNominations([]);

    fetchNominations(
      stateId,
      districtId || "",
      assemblyId || "",
      ballotId,
      mandalamId || ""
    )
      .then((data) => setNominations(data || []))
      .catch(() => setError(ERROR_LOAD_NOMINATIONS))
      .finally(() => {
        setLoading(false);
        setGlobalLoading(false);
      });
  }, [stateId, ballotId, districtId, assemblyId, mandalamId]);

  /* ---------- Scroll reset ---------- */
  useEffect(() => {
    if (tableRef.current) {
      tableRef.current.scrollTop = 0;
    }
  }, [ballotId, districtId, assemblyId, mandalamId]);

  /* ---------- No state guard ---------- */
  if (!stateId) {
    return (
      <div className="main-container">
        <h2 className="section-title">{PAGE_TITLE_VIEW_NOMINATION}</h2>
        <p>
          {NO_STATE_SELECTED_MESSAGE.split("Home")[0]}
          <Link to={ROUTE_HOME}>Home</Link> page first.
        </p>
      </div>
    );
  }

  return (
    <div className="main-container">
      <h2 className="section-title">{PAGE_TITLE_VIEW_NOMINATION}</h2>

   <div
  style={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px",
    flexWrap: "wrap",
    marginBottom: "16px",
  }}
>
  <span className="iyc-badge" style={{ marginBottom: 0 }}>
    State: <strong>{storedStateName}</strong>
  </span>

<div style={{ display: "flex", gap: 12 }}>
  {isChhattisgarh && (
    <button
      className="iyc-primary-btn"
      type="button"
      onClick={() => setShowPdfModal(true)}
    >
      View Nomination List
    </button>
  )}

  <button
    className="iyc-primary-btn"
    type="button"
    onClick={handleReferralCount}
  >
    Show Referrer Count
  </button>
</div>
</div>

{/* ---------- Ballot ---------- */}
<div className="form-row" style={{ marginBottom: 16 }}>
  <SelectInput
    label={CHOOSE_BALLOT_LABEL}
    value={ballotId}
    options={ballots}
    onChange={(e) => setBallotId(e.target.value)}
  />
</div>

{/* ---------- Dynamic Dropdowns ---------- */}
<div className="form-row" style={{ marginBottom: 16 }}>
  {selectConfig
    .filter((cfg) => cfg.requiredFor.includes(ballotId))
    .map((cfg) => (
      <SelectInput
        key={cfg.key}
        label={cfg.label}
        value={cfg.value}
        options={cfg.options}
        onChange={(e) => cfg.setValue(e.target.value)}
        disabled={cfg.disabled}
      />
    ))}
</div>

      {loading && <p>{LOADING_NOMINATIONS_MESSAGE}</p>}
      {error && <p className="error-text">❌ {error}</p>}

      {!loading && nominations.length > 0 && (
        <div className="scroll-top-container" ref={tableRef}>
          <div className="scroll-top-inner">
            <table className="common-table">
              <thead>
                <tr>
                  {TABLE_HEADERS_NOMINATIONS.map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {nominations.map((n, i) => (
                  <tr key={i}>
                    <td>{n.bsn}</td>
                    <td className="member-id">{n.memberId}</td>
                    <td>{n.firstName} {n.lastName}</td>
                    <td>{n.gender}</td>
                    <td>{n.category}</td>
                    <td
                      className={
                        NOMINATION_STATUS_CLASSES[n.nominationStatus] || ""
                      }
                    >
                      {n.nominationStatus || "-"}
                    </td>
                    <td>{n.remark || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && ballotId && nominations.length === 0 && !error && (
        <p>{NO_NOMINATIONS_FOUND_MESSAGE}</p>
      )}

      {/* ---------- NEW: Referrer Count Modal ---------- */}
    {showReferrerModal && (
  <div
    className="iyc-modal-overlay"
    onClick={() => setShowReferrerModal(false)}
  >
    <div
      className="iyc-modal-card iyc-referrer-modal"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="iyc-modal-header">
        <div>
          <h3 className="iyc-modal-title">Referrer Count</h3>
          <p className="iyc-modal-subtitle">
            State: <strong>{storedStateName}</strong>
          </p>
        </div>

        <button
          className="iyc-modal-close"
          type="button"
          onClick={() => setShowReferrerModal(false)}
          aria-label="Close"
        >
          ×
        </button>
      </div>

      <div className="iyc-modal-body">
        {referralLoading && <p>Loading referrer count...</p>}

        {!referralLoading && referralCounts.length === 0 && (
          <p>No referrer count data available.</p>
        )}

        {!referralLoading && referralCounts.length > 0 && (
          <div className="table-container">
            <table className="common-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Referrer</th>
                  <th>TOTAL</th>
                  <th>DP</th>
                  <th>DGS</th>
                  <th>ASSEMBLY</th>
                  <th>BLOCK</th>
                </tr>
              </thead>

              <tbody>
                {referralCounts.map((row, index) => (
                  <tr key={`${row.referrer || "ref"}-${index}`}>
                    <td>{index + 1}</td>
                    <td>{row.referrer || "-"}</td>
                    <td>{row.total ?? 0}</td>
                    <td>{row.dp ?? 0}</td>
                    <td>{row.dgs ?? 0}</td>
                    <td>{row.assembly ?? 0}</td>
                    <td>{row.block ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  </div>
)}
{/* ---------- Nomination List PDF Modal ---------- */}
{showPdfModal && (
  <div
    className="iyc-modal-overlay"
    onClick={() => setShowPdfModal(false)}
  >
    <div
      className="iyc-modal-card"
      onClick={(e) => e.stopPropagation()}
      style={{
        width: "95%",
        maxWidth: "1200px",
        height: "90vh",
      }}
    >
      <div className="iyc-modal-header">
        <div>
          <h3 className="iyc-modal-title">
            Nomination List for {storedStateName}
          </h3>

          <p className="iyc-modal-subtitle">
            State: <strong>{storedStateName}</strong>
          </p>
        </div>

        <button
          className="iyc-modal-close"
          type="button"
          onClick={() => setShowPdfModal(false)}
        >
          ×
        </button>
      </div>

      <div
        className="iyc-modal-body"
        style={{ padding: 0, height: "calc(90vh - 80px)" }}
      >
        <iframe
          src={pdfUrl}
          title="Nomination List for {storedStateName}"
          width="100%"
          height="100%"
          style={{
            border: "none",
            borderRadius: "0 0 12px 12px",
          }}
          allowFullScreen
        />
      </div>
    </div>
  </div>
)}
    </div>
  );
}